import { Service, signal } from '@angular/core';
import { Company, CompanyContext, CompanyContextInfo } from '@models/companies';
import { Project, ProjectContext, ProjectContextInfo } from '@models/projects';
import { TradeTemplate } from '@models/trades';
import { LoggedInUser } from '@models/user';

@Service()
export class DataContext {
  readonly loggedInUser = signal<LoggedInUser | null>(null);
  readonly projectContext = signal<ProjectContext | undefined>(undefined);
  readonly companyContext = signal<CompanyContext | undefined>(undefined);

  // The project the app is currently navigating to; async loads for any other project are obsolete and ignored.
  private requestedProjectId: string | undefined;
  private requestedCompanyId: string | undefined;

  setLoggedInUser(user: LoggedInUser): void {
    this.loggedInUser.set(user);
  }

  clearUser(): void {
    this.loggedInUser.set(null);
  }

  setProjectContext(info: ProjectContextInfo): void {
    this.requestedProjectId = info.id;
    this.projectContext.set(new ProjectContext(info));
  }

  clearProjectContext(): void {
    this.requestedProjectId = undefined;
    this.projectContext.set(undefined);
  }

  /**
   * Marks `projectId` as the requested project and returns its context only when it belongs to that project.
   * A context left over from another project (e.g. after a browser history jump) is cleared so it can never
   * decide access for this project.
   */
  matchProjectContext(projectId: string): ProjectContext | undefined {
    const context = this.projectContext();
    if (context && context.id !== projectId) {
      this.clearProjectContext();
    }

    this.requestedProjectId = projectId;
    return context?.id === projectId ? context : undefined;
  }

  /** False once navigation has moved on to another project, meaning results loaded for `projectId` are obsolete. */
  isRequestedProject(projectId: string): boolean {
    return this.requestedProjectId === projectId;
  }

  completeProjectContext(project: Project): void {
    if (!this.isRequestedProject(project.Id)) {
      return;
    }

    const existing = this.projectContext();
    const context =
      existing && existing.id === project.Id
        ? existing
        : new ProjectContext({
            id: project.Id,
            projectName: project.Name,
            hasAdminAccess: project.IsCurrentUserAdmin ?? false,
            hasClientAccess: project.hasClientAccess ?? false,
          });
    context.completeFromProject(project);
    this.projectContext.set(context);
  }

  setClientAccess(projectId: string, value: boolean): void {
    const context = this.projectContext();
    if (context?.id === projectId) {
      context.hasClientAccess.set(value);
    }
  }

  setCompanyContext(info: CompanyContextInfo): void {
    this.requestedCompanyId = info.id;
    this.companyContext.set(new CompanyContext(info));
  }

  clearCompanyContext(): void {
    this.requestedCompanyId = undefined;
    this.companyContext.set(undefined);
  }

  /** Same as `matchProjectContext`, but for the company in the route. */
  matchCompanyContext(companyId: string): CompanyContext | undefined {
    const context = this.companyContext();
    if (context && context.id !== companyId) {
      this.clearCompanyContext();
    }

    this.requestedCompanyId = companyId;
    return context?.id === companyId ? context : undefined;
  }

  isRequestedCompany(companyId: string): boolean {
    return this.requestedCompanyId === companyId;
  }

  completeCompanyContext(company: Company): void {
    if (!this.isRequestedCompany(company.Id)) {
      return;
    }

    const existing = this.companyContext();
    const context =
      existing && existing.id === company.Id
        ? existing
        : new CompanyContext({
            id: company.Id,
            companyName: company.Name,
          });
    context.completeFromCompany(company, this.loggedInUser()?.id);
    this.companyContext.set(context);
  }

  updateProjectTrades(trades: TradeTemplate[] | undefined): void {
    if (trades === undefined) {
      return;
    }

    const context = this.projectContext();
    if (!context) {
      return;
    }

    context.trades.set(trades);
    const project = context.project();
    if (project) {
      context.project.set({ ...project, Trades: trades });
    }
  }
}
